import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as Yup from 'yup';

const UpdateName = ({ currentMode, setCurrentMode }) => {
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [allFields, setAllFields] = useState({});

  const validationSchema = Yup.object({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
  });

  const postId = useMemo(() => {
    // Get the post ID from the DOM. It's on the body tag as a class "postid-123" 
    const postId = document.body.className.match(/postid-(\d+)/);
    if (postId) {
      return + postId[1];
    }
  }, []);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await axios.get('/wp-json/draught/v1/fields_name');
        response.data = Object.fromEntries(Object.entries(response.data).sort((a, b) => a[1].position - b[1].position));

        setAllFields(response.data);
        const initial = {};
        for (const key in response.data) {
          initial[key] = response.data[key].default;
        }

        setFormValues(initial);
      } catch (error) {
        console.error('Error fetching fields:', error);
        setErrorMessage('Failed to load fields.');
      }
    };
    fetchFields();
  }, []);

  const validate = async (values) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const newErrors = error.inner.reduce((acc, currentError) => {
        acc[currentError.path] = currentError.message;
        return acc;
      }, {});
      setErrors(newErrors);
      return false;
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const isValid = await validate(formValues);
      if (isValid) {
        axios.post('/wp-json/draught/v1/update-user-name', formValues)
          .then(response => {
            if (response.data.status === "success") {
              // The path, not the full url, no anchor or query string
              const redirectUrl = window.location.pathname + "?nameupdate=true";
              window.location.href = redirectUrl;
            }
          })
          .catch(error => {
            setErrorMessage(error.response?.data?.message || 'Update failed. Please check your details.');
          })
          .finally(() => setIsSubmitting(false));
      } else {
        setIsSubmitting(false);
      }
    }
    catch (error) {
      console.error('Error submitting form:', { error });
      setErrorMessage('Failed to submit form.');
      setIsSubmitting(false);
    }
  };

  return (
    <div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {Object.keys(allFields).map((key) => {
          const field = allFields[key];
          return (
            <>
              {(field.type === 'text'
                || field.type === 'email'
                || field.type === 'password'
              ) &&
                <div key={key} className='flex flex-col'>
                  <label htmlFor={key}>{field.title}</label>
                  <input
                    type={field.type}
                    name={key}
                    value={formValues[key] ?? ''}
                    onChange={handleChange}
                    className={field.type === 'select' ? "p-2 py-3 border-[#ccc]" : ''}
                  />
                  {errors[key] && <div className="text-red-500">{errors[key]}</div>}
                </div>}
              {field.type === 'select' &&
                <div key={key} className='flex flex-col'>
                  <label htmlFor={key}>{field.title}</label>
                  <select
                    name={key}
                    value={formValues[key] ?? ''}
                    onChange={handleChange}
                    className="p-2 py-3 border-[#ccc]"
                  >
                    <option value="">Select an option</option>
                    {field.options.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors[key] && <div className="text-red-500">{errors[key]}</div>}

                </div>}
              {field.type === 'checkbox' &&
                <div key={key} className='flex flex-col'>
                  {field.options.map((option, index) =>
                    <label htmlFor={key}>
                      <input
                        type="checkbox"
                        name={key}
                        value={option}
                        onChange={handleChange}
                        className="p-2 py-3 border-[#ccc]"
                      />
                      <span className='ml-2 inline-block max-w-[500px]'
                        dangerouslySetInnerHTML={{
                          __html: option
                        }} />
                    </label>
                  )}
                  {errors[key] && <div className="text-red-500">{errors[key]}</div>}
                </div>
              }
            </>
          );

        })}


        <input type="hidden" name="postId" value={postId} />
        {errorMessage && <div className="text-red-500">{errorMessage}</div>}
        <button type="submit" disabled={isSubmitting} className={`mt-4 text-white ${isSubmitting ? 'opacity-50' : ''}`}>
          {isSubmitting ? 'Send...' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default UpdateName;
